import { z } from 'zod';

/**
 * SIL-2535 - reseller Inventory & Margin pillar, first screen:
 * GET /api/client/inventory-margin.
 *
 * Per unit line: cost basis (asset_financial.purchase_price_usd x quantity) vs
 * current FMV (fmv_med_usd x quantity), days held from
 * COALESCE(in_service_at, purchased_at), unrealized margin, aging bucket.
 * Membership comes from v_client_asset_book_value so every total reconciles
 * with the Finance book-value numbers (`reconciliation`).
 *
 * Honest-data rule: a margin exists only where BOTH cost and FMV exist. A
 * rollup's margin covers only its matched lines (`matchedUnits`), never a
 * partial numerator over a full denominator.
 */

export const AGING_BUCKETS = ['0-30', '31-60', '61-90', '90+', 'unknown'] as const;
export const agingBucketSchema = z.enum(AGING_BUCKETS);
export type AgingBucket = z.infer<typeof agingBucketSchema>;

export const inventoryMarginQuerySchema = z.object({
  bucket: agingBucketSchema.optional(),
  limit: z.coerce.number().int().min(1).max(500).default(100),
});

const numOrNull = z.number().nullable();

export const inventoryMarginUnitSchema = z.object({
  assetId: z.string(),
  assetClassId: z.string().nullable(),
  assetClassName: z.string().nullable(),
  serialNumber: z.string().nullable(),
  quantity: z.number().int(),
  /** Line total (per-unit purchase price x quantity); null when no purchase price is recorded. */
  costBasisUsd: numOrNull,
  /** Line total (per-unit FMV x quantity); null when unpriced. */
  fmvUsd: numOrNull,
  unrealizedMarginUsd: numOrNull,
  /** Fraction of cost (0.25 = +25%); null when cost or FMV is missing or cost is 0. */
  unrealizedMarginPct: numOrNull,
  daysHeld: z.number().int().nullable(),
  agingBucket: agingBucketSchema,
});
export type InventoryMarginUnit = z.infer<typeof inventoryMarginUnitSchema>;

/** Totals shared by the summary, every bucket and every rollup group. */
const totalsShape = {
  /** Units (SUM(quantity)), not asset rows. */
  units: z.number(),
  costBasisUsd: z.number(),
  fmvUsd: z.number(),
  /** Units with BOTH cost and FMV - the only lines margin is computed over. */
  matchedUnits: z.number(),
  matchedCostBasisUsd: z.number(),
  matchedFmvUsd: z.number(),
  unrealizedMarginUsd: numOrNull,
  unrealizedMarginPct: numOrNull,
  avgDaysHeld: numOrNull,
};

export const inventoryMarginSummarySchema = z.object(totalsShape);
export type InventoryMarginSummary = z.infer<typeof inventoryMarginSummarySchema>;

export const inventoryMarginBucketSchema = z.object({
  bucket: agingBucketSchema,
  ...totalsShape,
});
export type InventoryMarginBucket = z.infer<typeof inventoryMarginBucketSchema>;

export const inventoryMarginGroupSchema = z.object({
  /** Null only for the lot/site "Unassigned" group. */
  groupId: z.string().nullable(),
  groupLabel: z.string(),
  ...totalsShape,
});
export type InventoryMarginGroup = z.infer<typeof inventoryMarginGroupSchema>;

export const inventoryMarginResponseSchema = z.object({
  generatedAt: z.string(),
  summary: inventoryMarginSummarySchema,
  buckets: z.array(inventoryMarginBucketSchema),
  rollups: z.object({
    class: z.array(inventoryMarginGroupSchema),
    lot: z.array(inventoryMarginGroupSchema),
    team: z.array(inventoryMarginGroupSchema),
    site: z.array(inventoryMarginGroupSchema),
  }),
  /** Per-unit lines, longest held first, optionally narrowed to one aging bucket. */
  units: z.array(inventoryMarginUnitSchema),
  unitsTotal: z.number().int(),
  unitsTruncated: z.boolean(),
  /**
   * Tie-out to v_client_asset_book_value: the view's gross book value over
   * non-estimate rows (purchase price recorded) equals the cost basis over the
   * same rows.
   */
  reconciliation: z.object({
    bookGrossNonEstimateUsd: z.number(),
    costBasisNonEstimateUsd: z.number(),
  }),
});
export type InventoryMarginResponse = z.infer<typeof inventoryMarginResponseSchema>;
